import { APIError, apiRequest } from "@/lib/api";
import type { ProProviderConnection } from "@/lib/pro";

export type WorkspaceRole = "viewer" | "admin" | "owner";

export type WorkspacePermission =
  | "view_wallets"
  | "view_jobs"
  | "view_pooled_demand"
  | "view_resources"
  | "view_workloads"
  | "view_costs"
  | "view_recommendations"
  | "manage_providers"
  | "launch_compute"
  | "manage_alerts"
  | "execute_low_risk_actions"
  | "execute_high_risk_actions"
  | "approve_actions"
  | "invite_users"
  | "submit_commitments"
  | "billing"
  | "legal_commitments"
  | "manage_roles"
  | "manage_workspace";

export type WorkspaceMember = {
  id: string;
  userId?: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  status: "active" | "invited" | string;
  invitedAt: string;
  joinedAt?: string;
};

export type WorkspacePendingInvite = {
  id: string;
  workspaceId: string;
  workspaceName: string;
  companyName: string;
  email: string;
  role: WorkspaceRole;
  invitedByUserId?: string;
  invitedAt: string;
};

export type WorkspacePublic = {
  id: string;
  workspaceType: "personal" | "team" | string;
  name: string;
  slug: string;
  status: "active" | string;
  companyName: string;
  billingEmail: string;
  members: WorkspaceMember[];
  rolePermissions: Record<WorkspaceRole, WorkspacePermission[]>;
  currentMemberId: string;
  currentRole: WorkspaceRole;
  currentPermissions: WorkspacePermission[];
};

export type WorkspacePlan = {
  code: "free" | "pro" | string;
  name: string;
  description: string;
  billingStatus: string;
  billingSource: string;
  currency: string;
  monthlyBasePriceMicros: number;
  monthlySeatPriceMicros: number;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  updatedAt?: string;
};

export type WorkspaceEntitlement = {
  enabled: boolean;
  limitValue?: string;
};

export type WorkspaceCurrentResponse = {
  workspace: WorkspacePublic;
  plan: WorkspacePlan;
  entitlements: Record<string, WorkspaceEntitlement>;
  pendingInvites?: WorkspacePendingInvite[];
  user?: {
    id: string;
    username: string;
    email: string;
    name: string;
  };
  isOwner: boolean;
  canManageOrg: boolean;
};

export type WorkspaceOrgStats = {
  memberCount: number;
  activeMemberCount: number;
  providerCount: number;
  activeProviderCount: number;
  networkProviderCount: number;
  storageObjectCount: number;
  activeRuntimeCount: number;
  availableCreditMicros: number;
  reservedCreditMicros: number;
  spentCreditMicros: number;
  totalCreditMicros: number;
  earnedCreditMicros: number;
  pendingPayoutMicros: number;
  budgetLimitMicros: number;
  budgetFundedMicros: number;
  budgetRemainingMicros: number;
};

export type WorkspaceMemberUsage = {
  memberId: string;
  userId?: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  status: "active" | "invited" | string;
  totalCreditMicros: number;
  availableCreditMicros: number;
  reservedCreditMicros: number;
  spentCreditMicros: number;
  workloadCount: number;
  activeWorkloadCount: number;
  completedWorkloadCount: number;
  failedWorkloadCount: number;
  activeRuntimeCount: number;
  runtimeTurnCount: number;
  storageObjectCount: number;
  storageBytes: number;
  providerCount: number;
  activeProviderCount: number;
  networkProviderCount: number;
  earnedCreditMicros: number;
  pendingPayoutMicros: number;
  budgetId?: string;
  budgetSource?: string;
  budgetStatus?: "active" | "paused" | string;
  budgetLimitMicros: number;
  budgetFundedMicros: number;
  budgetRemainingMicros: number;
  budgetUpdatedAt?: string;
  lastActivityAt?: string;
};

export type WorkspaceSecurityEvent = {
  id: string;
  eventType: string;
  severity: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

export type WorkspaceOrgResponse = {
  workspace: WorkspacePublic;
  plan: WorkspacePlan;
  entitlements: Record<string, WorkspaceEntitlement>;
  stats: WorkspaceOrgStats;
  memberUsage: WorkspaceMemberUsage[];
  providers: ProProviderConnection[];
  securityEvents: WorkspaceSecurityEvent[];
  pendingInvites?: WorkspacePendingInvite[];
};

export type WorkspaceResponse = {
  workspace: WorkspacePublic;
  pendingInvites?: WorkspacePendingInvite[];
};

export type WorkspaceDashboardState = {
  workspace: WorkspacePublic | null;
  organization: WorkspaceOrgResponse | null;
  pendingInvites: WorkspacePendingInvite[];
};

export const WORKSPACE_API = "/api/wallet/accounts?action=workspace";

export const ROLE_LABELS: Record<WorkspaceRole, string> = {
  viewer: "Viewer",
  admin: "Admin",
  owner: "Workspace owner",
};

export const ROLE_HINTS: Record<WorkspaceRole, string> = {
  viewer: "Can view wallets, usage, pooled demand, jobs, and reports.",
  admin: "Can manage providers, launch compute, create demand, and invite users.",
  owner: "Full workspace control, billing, legal commitments, and role management.",
};

export const PERMISSION_LABELS: Record<WorkspacePermission, string> = {
  view_wallets: "View wallets",
  view_jobs: "View jobs",
  view_pooled_demand: "View pooled demand",
  view_resources: "View resources",
  view_workloads: "View workloads",
  view_costs: "View costs",
  view_recommendations: "View recommendations",
  manage_providers: "Manage providers",
  launch_compute: "Launch compute",
  manage_alerts: "Manage alerts",
  execute_low_risk_actions: "Execute low-risk actions",
  execute_high_risk_actions: "Execute high-risk actions",
  approve_actions: "Approve actions",
  invite_users: "Invite users",
  submit_commitments: "Submit commitments",
  billing: "Billing",
  legal_commitments: "Legal commitments",
  manage_roles: "Manage roles",
  manage_workspace: "Manage workspace",
};

export const ROLE_PERMISSION_OPTIONS = Object.keys(PERMISSION_LABELS) as WorkspacePermission[];

export const DEFAULT_ROLE_PERMISSIONS: Record<WorkspaceRole, WorkspacePermission[]> = {
  viewer: [
    "view_wallets",
    "view_jobs",
    "view_pooled_demand",
    "view_resources",
    "view_workloads",
    "view_costs",
    "view_recommendations",
  ],
  admin: [
    "view_wallets",
    "view_jobs",
    "view_pooled_demand",
    "view_resources",
    "view_workloads",
    "view_costs",
    "view_recommendations",
    "manage_providers",
    "launch_compute",
    "manage_alerts",
    "execute_low_risk_actions",
    "invite_users",
    "submit_commitments",
  ],
  owner: [
    "view_wallets",
    "view_jobs",
    "view_pooled_demand",
    "view_resources",
    "view_workloads",
    "view_costs",
    "view_recommendations",
    "manage_providers",
    "launch_compute",
    "manage_alerts",
    "execute_low_risk_actions",
    "execute_high_risk_actions",
    "approve_actions",
    "invite_users",
    "submit_commitments",
    "billing",
    "legal_commitments",
    "manage_roles",
    "manage_workspace",
  ],
};

export async function readCurrentWorkspace() {
  const payload = await apiRequest<WorkspaceCurrentResponse>("/api/v1/workspace/current", {
    method: "GET",
    cache: "no-store",
    timeoutMs: 15000,
  });
  return {
    ...payload,
    workspace: normalizeWorkspace(payload.workspace),
    pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites),
  };
}

export async function readWorkspaceOrg() {
  const payload = await apiRequest<WorkspaceOrgResponse>("/api/v1/workspace/org", {
    method: "GET",
    cache: "no-store",
    timeoutMs: 20000,
  });
  return {
    ...payload,
    workspace: normalizeWorkspace(payload.workspace),
    pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites),
  };
}

export async function setWorkspaceMemberBudget(input: {
  memberId: string;
  limitMicros?: number;
  limitCredits?: number;
  sourceType?: "owner_wallet" | string;
  status?: "active" | "paused" | string;
  note?: string;
}) {
  return apiRequest<WorkspaceOrgResponse>("/api/v1/workspace/org?org_action=member_budget", {
    method: "POST",
    body: {
      memberId: input.memberId,
      limitMicros: input.limitMicros,
      limitCredits: input.limitCredits,
      sourceType: input.sourceType || "owner_wallet",
      status: input.status || "active",
      note: input.note || "",
    },
    cache: "no-store",
    timeoutMs: 20000,
  });
}

export async function sendWorkspaceMemberCredits(input: {
  memberId?: string;
  email?: string;
  amountMicros?: number;
  amountCredits?: number;
  note?: string;
}) {
  return apiRequest<WorkspaceOrgResponse>("/api/v1/workspace/org?org_action=transfer_credits", {
    method: "POST",
    body: {
      memberId: input.memberId,
      email: input.email,
      amountMicros: input.amountMicros,
      amountCredits: input.amountCredits,
      idempotencyKey: `ui_workspace_credit_transfer_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      note: input.note || "",
    },
    cache: "no-store",
    timeoutMs: 20000,
  });
}

export async function readWorkspace() {
  const payload = await apiRequest<WorkspaceResponse>(WORKSPACE_API, { method: "GET" });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

function workspaceIDsMatch(left?: WorkspacePublic | null, right?: WorkspacePublic | null) {
  const leftID = String(left?.id || "").trim();
  const rightID = String(right?.id || "").trim();
  return Boolean(leftID && rightID && leftID === rightID);
}

export function resolveWorkspaceDashboardState(input: {
  organization?: WorkspaceOrgResponse | null;
  direct?: WorkspaceResponse | null;
  previousWorkspace?: WorkspacePublic | null;
  previousOrganization?: WorkspaceOrgResponse | null;
  previousInvites?: WorkspacePendingInvite[];
}): WorkspaceDashboardState {
  const workspace = input.direct?.workspace
    ?? input.organization?.workspace
    ?? input.previousWorkspace
    ?? input.previousOrganization?.workspace
    ?? null;
  const organization = workspaceIDsMatch(input.organization?.workspace, workspace)
    ? input.organization || null
    : null;
  const previousOrganization = workspaceIDsMatch(input.previousOrganization?.workspace, workspace)
    ? input.previousOrganization || null
    : null;
  return {
    workspace,
    // A fresh general workspace response is authoritative for role and roster.
    // Do not let stale owner-only analytics override it when the org request fails.
    organization: organization ?? (input.direct ? null : previousOrganization),
    pendingInvites: input.direct?.pendingInvites
      ?? input.organization?.pendingInvites
      ?? input.previousInvites
      ?? [],
  };
}

export async function updateWorkspaceProfile(input: {
  companyName: string;
  billingEmail: string;
}) {
  const payload = await apiRequest<WorkspaceResponse>(WORKSPACE_API, {
    method: "PATCH",
    body: {
      companyName: input.companyName,
      billingEmail: input.billingEmail,
    },
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export async function inviteWorkspaceMember(input: {
  name: string;
  email: string;
  role: WorkspaceRole;
}) {
  const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=invite`, {
    method: "POST",
    body: input,
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export async function acceptWorkspaceInvite(inviteId: string) {
  const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=accept_invite`, {
    method: "POST",
    body: { inviteId },
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export async function declineWorkspaceInvite(inviteId: string) {
  const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=decline_invite`, {
    method: "POST",
    body: { inviteId },
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export async function updateWorkspaceMemberRole(memberId: string, role: WorkspaceRole) {
  const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=member_role`, {
    method: "PATCH",
    body: { memberId, role },
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export async function removeWorkspaceMember(memberId: string) {
  const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=member`, {
    method: "DELETE",
    body: { memberId },
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export async function updateWorkspaceRolePermissions(
  rolePermissions: Record<WorkspaceRole, WorkspacePermission[]>,
) {
  const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=role_permissions`, {
    method: "PATCH",
    body: { rolePermissions: normalizeRolePermissions(rolePermissions) },
  });
  return { workspace: normalizeWorkspace(payload.workspace), pendingInvites: normalizeWorkspacePendingInvites(payload.pendingInvites) };
}

export function emptyWorkspace(): WorkspacePublic {
  return {
    id: "",
    workspaceType: "team",
    name: "",
    slug: "",
    status: "active",
    companyName: "",
    billingEmail: "",
    members: [],
    rolePermissions: normalizeRolePermissions(null),
    currentMemberId: "",
    currentRole: "viewer",
    currentPermissions: [],
  };
}

export function normalizeWorkspace(value: unknown): WorkspacePublic {
  const raw = value && typeof value === "object" ? value as Partial<WorkspacePublic> : {};
  const members = Array.isArray(raw.members) ? raw.members : [];
  const normalizedMembers: WorkspaceMember[] = members
    .map((member: any): WorkspaceMember => ({
      id: String(member?.id || ""),
      userId: String(member?.userId || ""),
      name: String(member?.name || ""),
      email: String(member?.email || "").toLowerCase(),
      role: normalizeRole(member?.role),
      status: member?.status === "invited" ? "invited" : "active",
      invitedAt: String(member?.invitedAt || ""),
      joinedAt: String(member?.joinedAt || ""),
    }))
    .filter((member: WorkspaceMember) => Boolean(member.id && member.email));
  return {
    id: String(raw.id || ""),
    workspaceType: String(raw.workspaceType || "team"),
    name: String(raw.name || raw.companyName || ""),
    slug: String(raw.slug || ""),
    status: String(raw.status || "active"),
    companyName: String(raw.companyName || raw.name || ""),
    billingEmail: String(raw.billingEmail || ""),
    members: normalizedMembers,
    rolePermissions: normalizeRolePermissions(raw.rolePermissions),
    currentMemberId: String(raw.currentMemberId || ""),
    currentRole: normalizeRole(raw.currentRole),
    currentPermissions: normalizePermissionList(raw.currentPermissions, []),
  };
}

export function normalizeWorkspacePendingInvites(value: unknown): WorkspacePendingInvite[] {
  return (Array.isArray(value) ? value : [])
    .map((invite: any): WorkspacePendingInvite => ({
      id: String(invite?.id || invite?.inviteId || ""),
      workspaceId: String(invite?.workspaceId || invite?.workspace_id || ""),
      workspaceName: String(invite?.workspaceName || invite?.workspace_name || invite?.companyName || invite?.company_name || "Workspace"),
      companyName: String(invite?.companyName || invite?.company_name || invite?.workspaceName || invite?.workspace_name || "Workspace"),
      email: String(invite?.email || "").toLowerCase(),
      role: normalizeRole(invite?.role),
      invitedByUserId: String(invite?.invitedByUserId || invite?.invited_by_user_id || ""),
      invitedAt: String(invite?.invitedAt || invite?.invited_at || ""),
    }))
    .filter((invite) => Boolean(invite.id && invite.workspaceId && invite.email));
}

export function normalizeRole(value: unknown): WorkspaceRole {
  if (value === "admin" || value === "owner" || value === "viewer") return value;
  return "viewer";
}

export function hasWorkspacePermission(workspace: WorkspacePublic, permission: WorkspacePermission): boolean {
  return workspace.currentRole === "owner" || workspace.currentPermissions.includes(permission);
}

export function normalizeRolePermissions(value: unknown): Record<WorkspaceRole, WorkspacePermission[]> {
  const source = value && typeof value === "object" ? value as Partial<Record<WorkspaceRole, unknown>> : {};
  return {
    viewer: normalizePermissionList(source.viewer, DEFAULT_ROLE_PERMISSIONS.viewer),
    admin: normalizePermissionList(source.admin, DEFAULT_ROLE_PERMISSIONS.admin),
    owner: [...DEFAULT_ROLE_PERMISSIONS.owner],
  };
}

export function normalizePermissionList(value: unknown, fallback: WorkspacePermission[]) {
  if (!Array.isArray(value)) return [...fallback];
  const permissions = value
    .map((item) => String(item || "") as WorkspacePermission)
    .filter((item) => ROLE_PERMISSION_OPTIONS.includes(item));
  return permissions.length ? permissions : [...fallback];
}

export function getRolePermissions(
  rolePermissions: Record<WorkspaceRole, WorkspacePermission[]> | undefined,
  role: WorkspaceRole,
) {
  return normalizeRolePermissions(rolePermissions)[role];
}

export function formatPermissions(permissions: WorkspacePermission[]) {
  return permissions.map((permission) => PERMISSION_LABELS[permission]).filter(Boolean).join(" · ");
}

export function workspaceErrorMessage(err: unknown) {
  if (err instanceof APIError) {
    if (err.status === 401 || err.code === "ERR_AUTH_REQUIRED") {
      return "Sign in again to manage this workspace.";
    }
    if (err.status === 403 || err.code === "ERR_FORBIDDEN") {
      return "Only workspace owners can view organisation controls.";
    }
    return err.detail || err.message || "Could not load organisation.";
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "Could not load organisation.";
}
